import { useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Avatar, cn, Textarea } from '@vwt/ui';
import { Bold, Code, Italic, Link2, List, ListOrdered, Quote } from 'lucide-react';
import { renderMarkdown } from '../lib/markdown.js';
import { useTranslation } from '../lib/i18n.js';
import { submitOnModEnter } from '../lib/hotkeys.js';
import { useApiQuery } from '../lib/api.js';

export function MarkdownView({ source, className }: { source: string; className?: string }) {
  const html = useMemo(() => renderMarkdown(source), [source]);
  // Sanitized by DOMPurify with a strict allowlist (no raw HTML, no images).
  return <div className={cn('vwt-markdown text-sm', className)} dangerouslySetInnerHTML={{ __html: html }} />;
}

/** Height the mention list needs (8 people) before it opens above the editor instead. */
const MENTION_LIST_SPACE = 300;

const tools = [
  { icon: Bold, label: 'Bold', wrap: ['**', '**'] },
  { icon: Italic, label: 'Italic', wrap: ['_', '_'] },
  { icon: Code, label: 'Code', wrap: ['`', '`'] },
  { icon: Link2, label: 'Link', wrap: ['[', '](https://)'] },
  { icon: List, label: 'Bulleted list', prefix: '- ' },
  { icon: ListOrdered, label: 'Numbered list', prefix: '1. ' },
  { icon: Quote, label: 'Quote', prefix: '> ' },
] as const;

/** The "@query" being typed right before the caret, if any (no spaces, at a word start). */
export function mentionAt(value: string, caret: number): { start: number; query: string } | null {
  const before = value.slice(0, caret);
  const match = /(^|[\s(])@([\p{L}\p{N}._-]{0,30})$/u.exec(before);
  if (!match) return null;
  return { start: caret - match[2]!.length - 1, query: match[2]! };
}

/** Stable mention syntax read by the server for notifications: @[Display Name](user:<uuid>). */
export function mentionMarkup(name: string, id: string): string {
  return `@[${name.replace(/[[\]]/g, '')}](user:${id})`;
}

/**
 * Accessible Markdown editor: plain textarea with a formatting toolbar and sanitized preview.
 * Users never need Markdown knowledge for structured fields. Typing @ suggests people to mention.
 */
export function MarkdownEditor({
  value,
  onChange,
  placeholder,
  minRows = 5,
  onSubmit,
  id,
  invalid,
  ariaLabel,
  mentions = true,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  minRows?: number;
  onSubmit?: () => void;
  id?: string;
  invalid?: boolean;
  ariaLabel?: string;
  mentions?: boolean;
}) {
  const { t } = useTranslation();
  const [mode, setMode] = useState<'write' | 'preview'>('write');
  const autoId = useId();
  const textareaId = id ?? autoId;
  const listId = `${textareaId}-mentions`;
  const [mention, setMention] = useState<{ start: number; query: string } | null>(null);
  const [active, setActive] = useState(0);
  const textarea = useRef<HTMLTextAreaElement | null>(null);
  const people = useApiQuery('searchPrincipals', { query: { q: mention?.query ?? '', kinds: 'USER' } }, { enabled: mentions && !!mention });
  const options = mention ? (people.data?.data ?? []).filter((p) => p.kind === 'USER').slice(0, 8) : [];
  const open = mention !== null && options.length > 0;
  // Suggestions open below the editor, or above it when the editor sits near the bottom of the screen,
  // so they never cover the text being typed nor fall outside the viewport.
  const box = useRef<HTMLDivElement | null>(null);
  const [above, setAbove] = useState(false);
  useLayoutEffect(() => {
    if (!open || !box.current) return;
    const rect = box.current.getBoundingClientRect();
    setAbove(window.innerHeight - rect.bottom < MENTION_LIST_SPACE && rect.top > window.innerHeight - rect.bottom);
  }, [open]);

  useEffect(() => {
    setActive(0);
  }, [mention?.query]);

  // Radix dialogs and drawers handle Escape on the document in the capture phase, before React
  // sees the key. While suggestions are open, a window capture listener runs first and keeps
  // Escape from closing the surrounding dialog.
  useEffect(() => {
    if (!open) return;
    const onEscape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || event.target !== textarea.current) return;
      event.preventDefault();
      event.stopPropagation();
      setMention(null);
    };
    window.addEventListener('keydown', onEscape, true);
    return () => window.removeEventListener('keydown', onEscape, true);
  }, [open]);

  const apply = (tool: (typeof tools)[number]) => {
    const el = textarea.current;
    if (!el) return;
    const start = el.selectionStart;
    const end = el.selectionEnd;
    const selected = value.slice(start, end);
    let next: string;
    if ('wrap' in tool) next = value.slice(0, start) + tool.wrap[0] + selected + tool.wrap[1] + value.slice(end);
    else {
      const lineStart = value.lastIndexOf('\n', start - 1) + 1;
      next = value.slice(0, lineStart) + tool.prefix + value.slice(lineStart);
    }
    onChange(next);
    requestAnimationFrame(() => el.focus());
  };

  const track = (el: HTMLTextAreaElement) => {
    if (!mentions) return;
    setMention(el.selectionStart === el.selectionEnd ? mentionAt(el.value, el.selectionStart) : null);
  };

  const insert = (person: { id: string; name: string }) => {
    const el = textarea.current;
    if (!el || !mention) return;
    const caret = el.selectionStart;
    const markup = `${mentionMarkup(person.name, person.id)} `;
    onChange(value.slice(0, mention.start) + markup + value.slice(caret));
    setMention(null);
    const position = mention.start + markup.length;
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(position, position);
    });
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (open) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        setActive((i) => (i + (e.key === 'ArrowDown' ? 1 : options.length - 1)) % options.length);
        return;
      }
      if ((e.key === 'Enter' && !(e.metaKey || e.ctrlKey)) || e.key === 'Tab') {
        e.preventDefault();
        const person = options[active];
        if (person) insert(person);
        return;
      }
    }
    if (onSubmit) submitOnModEnter(e, onSubmit);
  };

  return (
    <div ref={box} className="relative rounded-[var(--radius)] border border-border-strong bg-surface focus-within:border-ring focus-within:ring-2 focus-within:ring-ring/30">
      <div className="flex items-center gap-0.5 border-b border-border px-1.5 py-1">
        <button type="button" className={cn('rounded px-2 py-0.5 text-xs', mode === 'write' ? 'bg-muted font-medium text-fg' : 'text-fg-muted')} onClick={() => setMode('write')}>
          {t('common.write')}
        </button>
        <button type="button" className={cn('rounded px-2 py-0.5 text-xs', mode === 'preview' ? 'bg-muted font-medium text-fg' : 'text-fg-muted')} onClick={() => setMode('preview')}>
          {t('common.preview')}
        </button>
        {mode === 'write' ? (
          <div className="ml-auto flex items-center gap-0.5" role="toolbar" aria-label="Formatting">
            {tools.map((tool) => (
              <button key={tool.label} type="button" className="rounded p-1 text-fg-subtle hover:bg-muted hover:text-fg" aria-label={tool.label} title={tool.label} onClick={() => apply(tool)}>
                <tool.icon className="size-3.5" />
              </button>
            ))}
          </div>
        ) : null}
      </div>
      {mode === 'write' ? (
        <Textarea
          ref={textarea}
          id={textareaId}
          aria-label={ariaLabel}
          aria-invalid={invalid || undefined}
          aria-autocomplete={mentions ? 'list' : undefined}
          aria-expanded={mentions ? open : undefined}
          aria-controls={open ? listId : undefined}
          aria-activedescendant={open ? `${listId}-${active}` : undefined}
          value={value}
          rows={minRows}
          placeholder={placeholder}
          onChange={(e) => {
            onChange(e.target.value);
            track(e.target);
          }}
          onClick={(e) => track(e.currentTarget)}
          onKeyUp={(e) => ['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(e.key) && track(e.currentTarget)}
          onBlur={() => setTimeout(() => setMention(null), 150)}
          onKeyDown={onKeyDown}
          className="rounded-none border-0 shadow-none focus-visible:ring-0"
        />
      ) : (
        <div className="min-h-24 px-3 py-2">{value.trim() ? <MarkdownView source={value} /> : <p className="text-sm text-fg-subtle">—</p>}</div>
      )}
      {open ? (
        <ul id={listId} role="listbox" aria-label={t('comments.mentionPeople')} className={cn('absolute left-2 z-30 w-64 overflow-hidden rounded-[var(--radius)] border border-border bg-surface p-1 shadow-lg', above ? 'bottom-full mb-1' : 'top-full mt-1')}>
          {options.map((person, index) => (
            <li
              key={person.id}
              id={`${listId}-${index}`}
              role="option"
              aria-selected={index === active}
              onMouseDown={(e) => {
                // Keep the textarea focused so the caret position stays valid.
                e.preventDefault();
                insert(person);
              }}
              onMouseEnter={() => setActive(index)}
              className={cn('flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm', index === active && 'bg-muted')}
            >
              <Avatar name={person.name} size="sm" />
              <span className="truncate">{person.name}</span>
            </li>
          ))}
        </ul>
      ) : null}
      <p className="border-t border-border px-2.5 py-1 text-[11px] text-fg-subtle">
        {t('common.markdownHint')}
        {mentions ? ` ${t('comments.mentionHint')}` : ''}
      </p>
    </div>
  );
}
